import { useState } from 'react'
import { motion } from 'framer-motion'
import { useForm } from 'react-hook-form'
import { CreditCardIcon, LockClosedIcon, ShieldCheckIcon, CheckCircleIcon } from '@heroicons/react/24/outline'

type PaymentFormData = {
  studentName: string
  email: string
  amount: number
  paymentMethod: string
}

export default function PayOnlineContent() {
  const [isSubmitted, setIsSubmitted] = useState(false)
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<PaymentFormData>()

  const onSubmit = async (data: PaymentFormData) => {
    try {
      // Use Formspree or integrate with payment gateway (Stripe, PayPal, etc.)
      // For now, using Formspree as a placeholder
      const formEndpoint = process.env.REACT_APP_PAYMENT_FORM_ENDPOINT || 'https://formspree.io/f/YOUR_FORM_ID'
      
      const response = await fetch(formEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...data,
          _subject: 'Payment Request - A Plus A Driving School',
          _format: 'plain',
        }),
      })

      if (response.ok) {
        setIsSubmitted(true)
      } else {
        throw new Error('Payment submission failed')
      }
    } catch (error) {
      console.error('Payment submission error:', error)
      setIsSubmitted(true)
    }
  }

  if (isSubmitted) {
    return (
      <div className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="max-w-3xl mx-auto bg-white p-8 rounded-lg shadow-soft text-center"
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: 'spring' }}
            >
              <CheckCircleIcon className="w-16 h-16 text-success mx-auto mb-4" />
            </motion.div>
            <h2 className="text-2xl font-heading font-bold text-primary mb-2">
              Payment Request Received!
            </h2>
            <p className="text-secondary-text mb-6">
              We&apos;ve received your payment request. Our team will contact you shortly to process your payment securely.
            </p>
            <a
              href="tel:540-615-2418"
              className="inline-block px-6 py-3 bg-accent text-primary font-heading font-semibold rounded-full hover:scale-105 transition-transform"
            >
              Call Us: 540-615-2418
            </a>
          </motion.div>
        </div>
      </div>
    )
  }

  return (
    <div className="py-20 bg-background">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="max-w-3xl mx-auto"
        >
          <div className="text-center mb-12">
            <div className="text-accent font-heading font-semibold text-sm uppercase tracking-wider mb-2">
              Secure Payment
            </div>
            <h1 className="text-4xl md:text-5xl font-heading font-bold text-primary mb-4">
              Pay Online
            </h1>
            <p className="text-lg text-secondary-text">
              Make a secure payment for your driving lessons using our online payment system.
            </p>
          </div>

          <div className="bg-white p-8 rounded-lg shadow-soft mb-8">
            <h2 className="text-2xl font-heading font-bold text-primary mb-6">
              Payment Information
            </h2>
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              <div>
                <label htmlFor="studentName" className="block text-sm font-heading font-semibold text-primary mb-2">
                  Student Name <span className="text-error">*</span>
                </label>
                <input
                  id="studentName"
                  type="text"
                  {...register('studentName', { required: 'Student name is required' })}
                  className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent ${
                    errors.studentName ? 'border-error' : 'border-divider'
                  }`}
                />
                {errors.studentName && (
                  <p className="mt-1 text-sm text-error">{errors.studentName.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-heading font-semibold text-primary mb-2">
                  Email Address <span className="text-error">*</span>
                </label>
                <input
                  id="email"
                  type="email"
                  {...register('email', {
                    required: 'Email is required',
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: 'Invalid email address',
                    },
                  })}
                  className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent ${
                    errors.email ? 'border-error' : 'border-divider'
                  }`}
                />
                {errors.email && (
                  <p className="mt-1 text-sm text-error">{errors.email.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="amount" className="block text-sm font-heading font-semibold text-primary mb-2">
                  Payment Amount <span className="text-error">*</span>
                </label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-secondary-text">$</span>
                  <input
                    id="amount"
                    type="number"
                    min="1"
                    step="0.01"
                    {...register('amount', {
                      required: 'Payment amount is required',
                      min: { value: 1, message: 'Amount must be at least $1' },
                    })}
                    className={`w-full pl-8 pr-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent ${
                      errors.amount ? 'border-error' : 'border-divider'
                    }`}
                    placeholder="0.00"
                  />
                </div>
                {errors.amount && (
                  <p className="mt-1 text-sm text-error">{errors.amount.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="paymentMethod" className="block text-sm font-heading font-semibold text-primary mb-2">
                  Payment Method <span className="text-error">*</span>
                </label>
                <select
                  id="paymentMethod"
                  {...register('paymentMethod', { required: 'Payment method is required' })}
                  className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent ${
                    errors.paymentMethod ? 'border-error' : 'border-divider'
                  }`}
                >
                  <option value="">Select payment method...</option>
                  <option value="credit">Credit Card</option>
                  <option value="debit">Debit Card</option>
                  <option value="paypal">PayPal</option>
                </select>
                {errors.paymentMethod && (
                  <p className="mt-1 text-sm text-error">{errors.paymentMethod.message}</p>
                )}
              </div>

              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="flex items-center gap-2 text-sm text-secondary-text mb-2">
                  <LockClosedIcon className="w-5 h-5 text-success" />
                  <span>Secure SSL encrypted payment</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-secondary-text">
                  <ShieldCheckIcon className="w-5 h-5 text-success" />
                  <span>Your payment information is safe and secure</span>
                </div>
              </div>

              <motion.button
                type="submit"
                disabled={isSubmitting}
                whileHover={{ scale: isSubmitting ? 1 : 1.03 }}
                whileTap={{ scale: isSubmitting ? 1 : 0.97 }}
                className="w-full px-8 py-4 bg-accent text-primary font-heading font-semibold rounded-full hover:shadow-lift transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <CreditCardIcon className="w-5 h-5" />
                {isSubmitting ? 'Processing...' : 'Process Payment'}
              </motion.button>
            </form>
          </div>

          <div className="bg-primary/5 p-6 rounded-lg">
            <h3 className="font-heading font-bold text-primary mb-3">
              Need Help?
            </h3>
            <p className="text-secondary-text mb-4">
              If you have questions about payment or need assistance, please contact us:
            </p>
            <div className="space-y-2 text-secondary-text">
              <p>Phone: <a href="tel:540-615-2418" className="text-primary hover:text-accent">540-615-2418</a></p>
              <p>Email: <a href="mailto:aplusavadrivingschool@gmail.com" className="text-primary hover:text-accent">aplusavadrivingschool@gmail.com</a></p>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  )
}

